import React, { useState, useEffect, useMemo } from 'react';
import { Box, Button, TextField, Typography, Alert, CircularProgress, useMediaQuery, useTheme } from '@mui/material';
import { useNavigate, useSearchParams, Link } from 'react-router-dom';
import { PasswordResetService } from '../api/generated/requests/services.gen';

const ResetPasswordPage: React.FC = () => {
  const [searchParams] = useSearchParams();
  const navigate = useNavigate();
  const token = searchParams.get('token');
  
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down('md'));

  const logoSvg = useMemo(() => (
    <svg
      width="56"
      height="56"
      viewBox="0 0 40 40"
      fill="none"
      xmlns="http://www.w3.org/2000/svg"
    >
      <defs>
        <linearGradient id="ustalone-logo-grad" x1="0" y1="0" x2="40" y2="40" gradientUnits="userSpaceOnUse">
          <stop stopColor="#06b6d4" />
          <stop offset="1" stopColor="#2563eb" />
        </linearGradient>
      </defs>
      <circle cx="20" cy="20" r="18" fill="url(#ustalone-logo-grad)" />
      <path
        d="M14 21.5L19 26L27 16"
        stroke="white"
        strokeWidth="2.2"
        strokeLinecap="round"
        strokeLinejoin="round"
      />
    </svg>
  ), []);

  const [newPassword, setNewPassword] = useState('');
  const [confirmPassword, setConfirmPassword] = useState('');
  const [loading, setLoading] = useState(false);
  const [validatingToken, setValidatingToken] = useState(true);
  const [tokenValid, setTokenValid] = useState(false);
  const [tokenError, setTokenError] = useState<string | null>(null);
  const [success, setSuccess] = useState(false);
  const [error, setError] = useState<string | null>(null);

  // Sprawdzenie poprawności tokena przy wczytaniu strony
  useEffect(() => {
    const validateToken = async () => {
      if (!token) {
        setTokenError('Brak tokenu resetowania hasła. Upewnij się, że używasz poprawnego linku.');
        setValidatingToken(false);
        return;
      }

      try {
        await PasswordResetService.getApiAuthPasswordValidate({
          token
        });
        setTokenValid(true);
        setValidatingToken(false);
      } catch (err: any) {
        console.error('Błąd podczas weryfikacji tokenu:', err);
        if (err.status === 410) {
          setTokenError('Link do resetowania hasła wygasł. Wygeneruj nowy link.');
        } else if (err.status === 404) {
          setTokenError('Nieprawidłowy token resetowania hasła. Upewnij się, że używasz poprawnego linku.');
        } else {
          setTokenError('Wystąpił błąd podczas weryfikacji tokenu. Spróbuj ponownie później.');
        }
        setValidatingToken(false);
      }
    };

    validateToken();
  }, [token]);

  const handleSubmit = async (event: React.FormEvent<HTMLFormElement>) => {
    event.preventDefault();
    setError(null);

    // Walidacja hasła
    if (newPassword.length < 8) {
      setError('Hasło musi mieć co najmniej 8 znaków.');
      return;
    }

    if (newPassword !== confirmPassword) {
      setError('Hasła nie są takie same.');
      return;
    }

    setLoading(true);

    try {
      if (!token) {
        throw new Error('Brak tokenu');
      }

      await PasswordResetService.postApiAuthPasswordReset({
        requestBody: {
          token,
          newPassword
        }
      });

      setSuccess(true);
      setLoading(false);
    } catch (err: any) {
      console.error('Błąd podczas resetowania hasła:', err);

      if (err.status === 410) {
        setError('Link do resetowania hasła wygasł. Wygeneruj nowy link.');
      } else if (err.status === 404) {
        setError('Nieprawidłowy token resetowania hasła.');
      } else {
        setError('Wystąpił błąd podczas resetowania hasła. Spróbuj ponownie później.');
      }

      setLoading(false);
    }
  };

  // Wyświetlanie ładowania podczas weryfikacji tokenu
  if (validatingToken) {
    return (
      <Box
        sx={{
          display: 'flex',
          flexDirection: { xs: 'column', md: 'row' },
          minHeight: '100vh',
        }}
      >
        <Box
          sx={{
            flex: 1,
            display: 'flex',
            flexDirection: 'column',
            justifyContent: 'center',
            alignItems: 'center',
            px: { xs: 4, md: 8 },
            py: 6,
          }}
        >
          <CircularProgress />
          <Typography variant="body1" sx={{ mt: 2 }}>
            Weryfikacja tokenu...
          </Typography>
        </Box>
        {!isMobile && (
          <Box
            sx={{
              flex: 1,
              backgroundImage: 'url(/crm-login.webp)',
              backgroundSize: 'cover',
              backgroundPosition: 'center',
            }}
          />
        )}
      </Box>
    );
  }

  return (
    <Box
      sx={{
        display: 'flex',
        flexDirection: { xs: 'column', md: 'row' },
        minHeight: '100vh',
      }}
    >
      {/* LEFT COLUMN */}
      <Box
        sx={{
          flex: 1,
          display: 'flex',
          flexDirection: 'column',
          justifyContent: 'space-between',
          px: { xs: 4, md: 8 },
          py: 6,
        }}
      >
        <Box sx={{ width: '100%', maxWidth: 480 }}>
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 2, mb: 4 }}>
            {logoSvg}
            <Typography
              variant="h5"
              sx={{
                fontWeight: 'bold',
                fontSize: '1.5rem',
                color: 'primary.main',
              }}
            >
              ustalone
            </Typography>
          </Box>

          <Typography variant="h6" fontWeight="bold" gutterBottom sx={{ lineHeight: 5}}>
            Resetowanie hasła
          </Typography>

          <Typography variant="body1" sx={{ mb: 4 }}>
            <strong>Ustalone</strong> to <strong>nowoczesny system CRM</strong>, który pomaga zarządzać relacjami z klientami, automatyzować sprzedaż i rozwijać Twój biznes.
          </Typography>

          {!tokenValid ? (
            <Box sx={{ width: '100%' }}>
              <Alert severity="error" sx={{ mb: 2 }}>
                {tokenError}
              </Alert>
              <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 3 }}>
                <Link to="/login" style={{ color: 'inherit', textDecoration: 'underline', fontSize: '0.875rem' }}>
                  Powrót do logowania
                </Link>
                <Button 
                  variant="contained" 
                  onClick={() => navigate('/reset-password-request')}
                >
                  Wygeneruj nowy link
                </Button>
              </Box>
            </Box>
          ) : success ? (
            <Box sx={{ width: '100%' }}>
              <Alert severity="success" sx={{ mb: 2 }}>
                Twoje hasło zostało pomyślnie zresetowane.
              </Alert>
              <Typography variant="body1" sx={{ mb: 3 }}>
                Możesz teraz zalogować się używając nowego hasła.
              </Typography>
              <Box sx={{ display: 'flex', justifyContent: 'flex-end', alignItems: 'center', mb: 3 }}>
                <Button 
                  variant="contained" 
                  onClick={() => navigate('/login')}
                >
                  Przejdź do logowania
                </Button>
              </Box>
            </Box>
          ) : (
            <form onSubmit={handleSubmit} noValidate>
              <Typography variant="body1" sx={{ mb: 3 }}>
                Wprowadź nowe hasło dla swojego konta.
              </Typography>

              {error && (
                <Alert severity="error" sx={{ mb: 2 }}>
                  {error}
                </Alert>
              )}

              <TextField
                label="Nowe hasło"
                type="password"
                fullWidth
                required
                variant="standard"
                value={newPassword}
                onChange={(e) => setNewPassword(e.target.value)}
                disabled={loading}
                sx={{ mb: 2 }}
                autoComplete="new-password"
              />

              <TextField
                label="Potwierdź nowe hasło"
                type="password"
                fullWidth
                required
                variant="standard"
                value={confirmPassword}
                onChange={(e) => setConfirmPassword(e.target.value)}
                disabled={loading}
                sx={{ mb: 3 }}
                autoComplete="new-password"
              />

              <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 3 }}>
                <Link to="/login" style={{ color: 'inherit', textDecoration: 'underline', fontSize: '0.875rem' }}>
                  Powrót do logowania
                </Link>
                <Button 
                  type="submit" 
                  variant="contained" 
                  disabled={loading || !newPassword || !confirmPassword}
                >
                  {loading ? 'Resetowanie...' : 'Ustaw nowe hasło'}
                </Button>
              </Box>
            </form>
          )}
        </Box>

        <Typography variant="caption" color="text.secondary">
          Copyright © 2025 Devlab Sp. z o.o.
        </Typography>
      </Box>

      {/* RIGHT COLUMN - Tylko obrazek */}
      {!isMobile && (
        <Box
          sx={{
            flex: 1,
            backgroundImage: 'url(/crm-login.webp)',
            backgroundSize: 'cover',
            backgroundPosition: 'center',
          }}
        />
      )}
    </Box>
  );
};

export default ResetPasswordPage;
